import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Container,
  VStack,
  HStack,
  Button,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Icon,
  Center,
  Box,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from "@chakra-ui/react";
import { ArrowBackIcon, ChevronDownIcon, EditIcon } from "@chakra-ui/icons";
import { FaPlay } from "react-icons/fa";
import { FiShare2 } from "react-icons/fi";
import { useFindOne, useFindMany, useUser, useGlobalAction } from "@gadgetinc/react";
import { api } from "../api";
import ScenarioDisplay from "../components/ScenarioDisplay";
import ShareScenarioModal from "../components/ShareScenarioModal";
import RunScenarioModal from "../components/RunScenarioModal";
import { VoiceSimShareModal } from "../components/VoiceSimShareModal";
import { buildScenarioDisplayProps } from "../lib/scenarioDisplayConfig";
import { buildGeneratedScenarioName } from "../lib/scenarioNaming";
import { hasAnyRole } from "../lib/roleUtils";

export default function ScenarioPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useUser(api);
  const [{ data: accessProfile }, getCurrentUserAccessProfile] = useGlobalAction(
    api.getCurrentUserAccessProfile
  );
  const roleSource = user?.roles || user?.roleList || [];
  const hasFallbackAdminAccess = hasAnyRole(roleSource, ["admin", "system-admin", "sysadmin"]);

  const [{ data: scenario, fetching, error }, refetch] = useFindOne(
    api.clinicalScenario,
    id,
    {
      select: {
        id: true,
        title: true,
        description: { markdown: true },
        levelOfCare: true,
        majorSystemInvolved: true,
        scenarioOutput: true,
        ageRange: true,
        ageBracket: true,
        difficulty: true,
        mechanismNatureOfInjury: true,
        createdAt: true,
        institution: true,
        isPublic: true,
        createdBy: {
          id: true,
        },
      },
    }
  );

  React.useEffect(() => {
    if (!user?.id) return;
    getCurrentUserAccessProfile({});
  }, [user?.id]);

  const [{ data: cohortMemberships }] = useFindMany(api.cohortMembership, {
    filter: {
      userId: { equals: user?.id },
      active: { equals: true },
    },
    select: {
      id: true,
      cohort: {
        id: true,
        name: true,
        displayName: true,
        institution: true,
      },
      primaryRole: true,
    },
    pause: !user?.id,
  });

  // Get instructor cohorts
  const instructorCohorts = cohortMemberships?.filter(
    membership => membership.primaryRole === 'instructor'
  ).map(membership => membership.cohort) || [];

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isRunModalOpen, setIsRunModalOpen] = useState(false);
  const [isVoiceSimShareOpen, setIsVoiceSimShareOpen] = useState(false);

  const canViewScenario = React.useMemo(() => {
    if (!scenario || !user?.id) return false;
    if (scenario.isPublic === true) return true;
    if (scenario.createdBy?.id && scenario.createdBy.id === user.id) return true;
    if (hasFallbackAdminAccess) return true;
    if (!accessProfile) return null;
    if (accessProfile?.roles?.isGadgetAdmin === true) return true;

    const canSeeInstitutionShared =
      accessProfile?.scenario?.canSeeSharedScenarios === true &&
      Boolean(accessProfile?.user?.institution) &&
      Boolean(scenario.institution) &&
      String(accessProfile.user.institution).trim().toLowerCase() ===
        String(scenario.institution).trim().toLowerCase();

    return canSeeInstitutionShared;
  }, [scenario, user?.id, accessProfile, hasFallbackAdminAccess]);

  React.useEffect(() => {
    const previousTitle = document.title;
    if (scenario) {
      const generatedName = buildGeneratedScenarioName(scenario, scenario?.title);
      document.title = generatedName || previousTitle;
    }
    return () => {
      document.title = previousTitle;
    };
  }, [scenario]);


  const handleRunScenario = () => {
    setIsRunModalOpen(true);
  };

  const handleShare = () => {
    setIsShareModalOpen(true);
  };

  const handleBack = () => {
    navigate("/my-scenarios");
  };

  const handleModifyScenario = () => {
    if (!scenario) return;

    const params = new URLSearchParams();
    const output = scenario.scenarioOutput;
    
    // Map levelOfCare to trainingLevel
    if (scenario.levelOfCare) {
      const levelMapping = {
        "EMT_Basic": "EMT-Basic",
        "EMT_Advanced": "EMT-Advanced",
        "Paramedic": "Paramedic",
        "Critical_Care": "Critical Care"
      };
      const trainingLevel = levelMapping[scenario.levelOfCare] || scenario.levelOfCare;
      params.append("trainingLevel", trainingLevel);
    }
    
    // Add difficulty
    if (scenario.difficulty) {
      params.append("difficulty", scenario.difficulty.toString());
    }
    
    // Add primaryBodySystem from majorSystemInvolved
    if (scenario.majorSystemInvolved) {
      params.append("primaryBodySystem", scenario.majorSystemInvolved);
    }
    
    // Add mechanismNatureOfInjury as natureMechanism
    if (scenario.mechanismNatureOfInjury) {
      params.append("natureMechanism", scenario.mechanismNatureOfInjury);
    }
    
    // Extract data from scenarioOutput if available
    if (output) {
      // Patient demographics
      if (output.patientInfo) {
        if (output.patientInfo.age) params.append("patientAge", output.patientInfo.age);
        if (output.patientInfo.sex) params.append("biologicalSex", output.patientInfo.sex);
      }
      
      // Chief complaint
      if (output.chiefComplaint) {
        params.append("chiefComplaint", output.chiefComplaint);
      }
      
      // Secondary complaint
      if (output.secondaryComplaint) {
        params.append("secondaryComplaint", output.secondaryComplaint);
      }
      
      // History fields
      if (output.history) {
        if (output.history.hpi) params.append("hpi", output.history.hpi);
        if (output.history.pmhx) params.append("pmhx", output.history.pmhx);
        if (output.history.medications) params.append("currentMedications", output.history.medications);
        if (output.history.allergies) params.append("allergies", output.history.allergies);
      }
      
      // Primary assessment fields  
      if (output.primaryAssessment) {
        if (output.primaryAssessment.mentalStatus) {
          params.append("levelOfConsciousness", output.primaryAssessment.mentalStatus);
        }
        if (output.primaryAssessment.airway) {
          params.append("airwayStatus", output.primaryAssessment.airway);
        }
        if (output.primaryAssessment.breathing) {
          params.append("breathingStatus", output.primaryAssessment.breathing);
        }
        if (output.primaryAssessment.circulation) {
          params.append("circulatoryStatus", output.primaryAssessment.circulation);
        }
      }
      
      // Secondary assessment fields
      if (output.secondaryAssessment) {
        if (output.secondaryAssessment.heent) params.append("heent", output.secondaryAssessment.heent);
        if (output.secondaryAssessment.respiratory) params.append("respiratory", output.secondaryAssessment.respiratory);
        if (output.secondaryAssessment.cardiovascular) params.append("cardiovascular", output.secondaryAssessment.cardiovascular);
        if (output.secondaryAssessment.abdomen) params.append("gi", output.secondaryAssessment.abdomen);
        if (output.secondaryAssessment.neuro) params.append("nervous", output.secondaryAssessment.neuro);
        if (output.secondaryAssessment.extremities) params.append("musculoskeletal", output.secondaryAssessment.extremities);
      }
      
      // Scene setup fields
      if (output.sceneSetup) {
        if (output.sceneSetup.location) {
          params.append("sceneDescription", output.sceneSetup.location);
        }
        if (output.sceneSetup.safetyIssues) {
          params.append("safetyConsideration", output.sceneSetup.safetyIssues);
        }
      }
    }

    navigate(`/scenario-generator?${params.toString()}`);
  };



  return (
    <Container maxW="container.xl" py={8}>
      <VStack spacing={6} align="stretch">
        <HStack mb={6} justify="space-between" className="no-print">
          <Button
            leftIcon={<ArrowBackIcon />}
            onClick={handleBack}
            variant="outline"
          >
            Back to Scenarios
          </Button>
          <HStack spacing={3}>
            <Button
              leftIcon={<Icon as={FaPlay} />}
              colorScheme="green"
              onClick={handleRunScenario}
              isDisabled={!canViewScenario}
            >
              Run Scenario
            </Button>
            {canViewScenario && (
              <Menu>
                <MenuButton 
                  as={Button}
                  leftIcon={<Icon as={FiShare2} />}
                  rightIcon={<ChevronDownIcon />}
                  colorScheme="teal"
                >
                  Share
                </MenuButton>
                <MenuList>
                  <MenuItem
                    icon={<Icon as={FiShare2} />}
                    onClick={handleShare}
                  >
                    Share for Evaluation
                  </MenuItem>
                  <MenuItem
                    icon={<Icon as={FiShare2} />}
                    onClick={() => setIsVoiceSimShareOpen(true)}
                  >
                    Share as Voice Sim
                  </MenuItem>
                </MenuList>
              </Menu>
            )}
            <Button
              leftIcon={<EditIcon />}
              colorScheme="blue"
              onClick={handleModifyScenario}
              variant="outline"
              isDisabled={!canViewScenario}
            >
              Modify
            </Button>
          </HStack>
        </HStack>

        {fetching && (
          <Center py={12}>
            <Spinner size="xl" color="blue.500" thickness="4px" />
          </Center>
        )}

        {error && (
          <Alert status="error">
            <AlertIcon />
            <AlertTitle>Error loading scenario</AlertTitle>
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}

        {scenario && (
          canViewScenario ? (
            <ScenarioDisplay
              {...buildScenarioDisplayProps({
                scenarioOutput: scenario.scenarioOutput,
                scenarioRecord: scenario,
                allowPrintControls: true,
              })}
            />
          ) : canViewScenario === null ? (
            <Center py={12}>
              <Spinner size="lg" color="blue.500" />
            </Center>
          ) : (
            <Alert status="error">
              <AlertIcon />
              <AlertTitle>Access denied</AlertTitle>
              <AlertDescription>
                You do not have permission to view this scenario.
              </AlertDescription>
            </Alert>
          )
        )}
      </VStack>

      {canViewScenario && (
        <ShareScenarioModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          scenario={scenario}
          cohort={null}
          user={user}
        />
      )}

      {canViewScenario && (
        <RunScenarioModal
          isOpen={isRunModalOpen}
          onClose={() => setIsRunModalOpen(false)}
          scenario={scenario}
          instructorCohorts={instructorCohorts}
        />
      )}

      {/* Voice Sim Share Modal */}
      {canViewScenario && (
        <VoiceSimShareModal
          isOpen={isVoiceSimShareOpen}
          onClose={() => setIsVoiceSimShareOpen(false)}
          scenario={scenario}
          instructorCohorts={instructorCohorts}
        />
      )}
    </Container>
  );
}
